import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
    Box,
    TextField,
    Divider,
    Typography,
    CircularProgress,
    alpha,
    FormControlLabel,
    Switch,
    Chip,
    Paper,
    Dialog,
    DialogContent,
    Slide,
} from "@mui/material";

import {
    Work,
    Psychology,
    Description,
    InfoOutlined,
    AutoAwesome,
    WorkspacePremium,
    LockOutlined,
} from "@mui/icons-material";

import Tooltip from "@mui/material/Tooltip";
import CustomInput from "../../components/common/CustomInput";
import CustomCard from "../../components/common/CustomCard";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomHeader from "../../components/common/CustomHeader";
import LinearProgress from "@mui/material/LinearProgress";

import { validateForm } from "../../validators/validateForm";
import { sanitizeField } from "../../validators/sanitizeField";
import { validateField } from "../../validators/validateField"

import { JD_FIELDS } from "../../constants/jdAnalysisFields"

import { jdApi } from "../../api/jobdescriptionApi";
import { questionApi } from "../../api/questionanswerApi";

import { useLocation, useNavigate } from "react-router-dom";


const REQUIRED_FIELDS = {
    job_title: "Job Title",
    company: "Company",
    keySkills: "Key Skills",
    summary: "Job Description",
    responsibilities: "Key Responsibilities",
    qualifications: "Educational Qualifications",
};

// ---------------- Slide Transition ----------------
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ---------------- Debounce Hook ----------------
function useDebounce(value, delay = 300) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debouncedValue;
}

// -------------- Section Header Component ---------------
function SectionHeader({ icon, title, subtitle, accent = "#667eea" }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3, mt: 1 }}>
            <Box
                sx={{
                    width: 40, height: 40, borderRadius: "10px",
                    background: `linear-gradient(135deg, ${accent}22 0%, ${accent}44 100%)`,
                    border: `1.5px solid ${accent}55`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: accent, flexShrink: 0, mt: 0.25,
                }}
            >
                {icon}
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight={700}
                    sx={{ color: "text.primary", lineHeight: 1.3, letterSpacing: "-0.01em" }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, display: "block" }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// -------------- Field Label ---------------
function FieldLabel({ children, required }) {
    return (
        <Typography variant="body2" fontWeight={600}
            sx={{ mb: 0.75, color: "text.secondary", fontSize: "0.8rem", letterSpacing: "0.03em", textTransform: "uppercase" }}>
            {children}
            {required && <span style={{ color: "#e53935", marginLeft: 3 }}>*</span>}
        </Typography>
    );
}

// -------------- Styled TextField (accepts error flag) ---------------
const fieldSx = (hasError) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        background: hasError ? alpha("#e53935", 0.03) : alpha("#667eea", 0.03),
        transition: "all 0.2s ease",
        "&:hover": { background: hasError ? alpha("#e53935", 0.05) : alpha("#667eea", 0.05) },
        "&.Mui-focused": {
            background: hasError ? alpha("#e53935", 0.05) : alpha("#667eea", 0.05),
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#e53935" : "#667eea",
                borderWidth: "1.5px",
            },
        },
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? alpha("#e53935", 0.5) : alpha("#667eea", 0.2),
    },
});

// -------------- Section Divider ---------------
function SectionDivider() {
    return <Divider sx={{ my: 4, borderStyle: "dashed", borderColor: alpha("#667eea", 0.15) }} />;
}

// -------------- Note Banner ---------------
function NoteBanner({ icon, message, accent = "#667eea" }) {
    return (
        <Paper elevation={0} sx={{
            display: "flex", alignItems: "flex-start", gap: 1.5,
            px: 2, py: 1.5, borderRadius: "10px",
            background: `linear-gradient(135deg, ${accent}10, ${accent}18)`,
            border: `1px solid ${accent}30`, mt: 1.5, mb: 0.5,
        }}>
            <Box sx={{ color: accent, display: "flex", mt: 0.1, flexShrink: 0 }}>{icon}</Box>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, fontSize: "0.82rem" }}>
                {message}
            </Typography>
        </Paper>
    );
}

// -------------- Two-column row ---------------
function TwoColRow({ children }) {
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            {children}
        </Box>
    );
}

// ================================================================
export default function JobDescriptionPage() {
    // ======================================================
    // Navigation / Refs
    // ======================================================

    const navigate = useNavigate();
    const location = useLocation();

    const id = location.state?.id;

    const saveLock = useRef(false);

    // ======================================================
    // State
    // ======================================================

    const [touched, setTouched] = useState({});

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [initialForm, setInitialForm] = useState(null);

    const [form, setForm] = useState({
        job_title: "",
        company: "",
        job_type: "",
        summary: "",
        domain: "",
        status: "",
        mode: "dynamic",
        jd_url: "",
        skillId: "",
        keySkills: "",
        additionalSkills: "",
        experienceId: "",
        responsibilities: "",
        educationId: "",
        qualifications: "",
    });

    const [errors, setErrors] = useState({});

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        type: "success",
    });

    // ======================================================
    // Memoized Values
    // ======================================================

    const hasChanges = useMemo(() => {
        if (!initialForm) return false;

        return Object.keys(form).some(
            (key) =>
                String(form[key] ?? "") !==
                String(initialForm[key] ?? "")
        );
    }, [form, initialForm]);

    const completion =
        useMemo(() => {

            const total =
                Object.keys(
                    JD_FIELDS
                ).length;

            const validFields =
                Object.keys(
                    JD_FIELDS
                ).filter(field => {

                    return !validateField({
                        ...JD_FIELDS[field],
                        value:
                            form[field],
                    });

                }).length;

            return Math.round(
                (validFields / total) *
                100
            );

        }, [form]);

    const hasValidationErrors =
        Object.values(errors)
            .some(Boolean);

    const canUpdate =
        hasChanges &&
        !hasValidationErrors &&
        !saving;

    // ======================================================
    // Callbacks
    // ======================================================

    const notify = useCallback(
        (message, type = "success") => {
            setSnackbar({
                open: true,
                message,
                type,
            });
        },
        []
    );

    const closeSnackbar = useCallback(() => {
        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    }, []);

    const markTouched = useCallback((field) => {
        setTouched((prev) => ({
            ...prev,
            [field]: true,
        }));
    }, []);

    const handleChange = useCallback(
        (field) => (e) => {

            const config =
                JD_FIELDS[field];

            const value =
                sanitizeField(
                    e.target.value,
                    config?.type
                );

            setForm(prev => ({
                ...prev,
                [field]: value,
            }));

            const error =
                validateField({
                    ...config,
                    value,
                });

            setErrors(prev => ({
                ...prev,
                [field]: error,
            }));
        },
        []
    );

    const isModified = useCallback(
        (field) => {
            if (!initialForm) return false;

            return (
                String(initialForm[field] ?? "") !==
                String(form[field] ?? "")
            );
        },
        [form, initialForm]
    );

    // ======================================================
    // Validation
    // ======================================================

    const validate = () => {

        const validationConfig = {};

        Object.keys(
            JD_FIELDS
        ).forEach(field => {
            validationConfig[field] =
                JD_FIELDS[field];
        });

        const {
            isValid,
            errors:
            validationErrors,
        } = validateForm(
            form,
            validationConfig
        );

        setErrors(prev => ({
            ...prev,
            ...validationErrors,
        }));

        return {
            isValid,
            validationErrors,
        };
    };
    // ======================================================
    // API Functions
    // ======================================================

    const generateJDQuestions = async (jobId) => {
        try {
            const response =
                await questionApi.generateQuestionsAsync({
                    job_id: jobId,
                });

            const { status, message } =
                response || {};

            if (status === 0)
                return {
                    success: false,
                    type: "network",
                    message:
                        "Network error during question generation.",
                };

            if (status >= 500)
                return {
                    success: false,
                    type: "server",
                    message:
                        message ||
                        "Server error while generating interview questions.",
                };

            if (status >= 400)
                return {
                    success: false,
                    type: "client",
                    message:
                        message ||
                        "Invalid request for question generation.",
                };

            if (
                status === 200 ||
                status === 201 ||
                status === 202
            ) {
                return {
                    success: true,
                    message:
                        message ||
                        "Interview questions generated successfully.",
                };
            }

            return {
                success: false,
                type: "unknown",
                message:
                    "Unexpected response during question generation.",
            };
        } catch (error) {
            console.error(error);

            return {
                success: false,
                type: "exception",
                message:
                    "Something went wrong while generating interview questions.",
            };
        }
    };

    const updateJDAndGenerateQuestions = async ({
        id,
        payload,
    }) => {
        const updateResponse =
            await jdApi.updateRecruiterJDDetail(
                id,
                payload
            );

        const { status, message } =
            updateResponse || {};

        if (status === 0)
            return {
                success: false,
                stage: "update",
                type: "network",
                message,
            };

        if (status >= 500)
            return {
                success: false,
                stage: "update",
                type: "server",
                message,
            };

        if (status >= 400)
            return {
                success: false,
                stage: "update",
                type: "client",
                message,
            };

        if (
            status !== 200 &&
            status !== 201
        ) {
            return {
                success: false,
                stage: "update",
                type: "unknown",
                message,
            };
        }

        let questionResult = null;

        if (payload.mode === "basic") {
            questionResult =
                await generateJDQuestions(id);
        }

        return {
            success: true,
            message,
            questionResult,
        };
    };

    // ======================================================
    // Effects
    // ======================================================

    useEffect(() => {
        const loadData = async () => {
            if (!id) {
                notify(
                    "No job ID provided",
                    "error"
                );

                navigate("/recruiter/landing");
                return;
            }

            try {
                setLoading(true);

                const response =
                    await jdApi.getRecruiterJDDetail(id);

                const {
                    status,
                    message,
                    data,
                } = response || {};

                if (status === 0) {
                    notify(
                        "Network error. Please check your connection.",
                        "warning"
                    );
                    return;
                }

                if (status >= 400) {
                    notify(
                        message ||
                        "Failed to load job description.",
                        "error"
                    );
                    return;
                }

                if (status === 200 && data) {
                    const formatted = {
                        job_title:
                            data.job_title ?? "",
                        company:
                            data.company ?? "",
                        job_type:
                            data.job_type ?? "",
                        summary:
                            data.summary ?? "",
                        domain:
                            data.domain ?? "",
                        status:
                            data.status ?? "",
                        mode:
                            data.mode ??
                            "dynamic",
                        jd_url:
                            data.jd_url ?? "",
                        skillId:
                            data.skill?.id ?? "",
                        keySkills:
                            data.skill
                                ?.keySkills ?? "",
                        additionalSkills:
                            data.skill
                                ?.additionalSkills ??
                            "",
                        experienceId:
                            data.experience?.id ??
                            "",
                        responsibilities:
                            data.experience
                                ?.responsibilities ??
                            "",
                        educationId:
                            data.education?.id ??
                            "",
                        qualifications:
                            data.education
                                ?.education ?? "",
                    };

                    setForm(formatted);
                    setInitialForm(formatted);
                    setModalOpen(true);
                }
            } catch (error) {
                console.error(error);

                notify(
                    "Something went wrong while loading job description.",
                    "error"
                );
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, navigate, notify]);

    useEffect(() => {
        const handleBeforeUnload = (
            event
        ) => {
            if (!hasChanges) return;

            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener(
            "beforeunload",
            handleBeforeUnload
        );

        return () => {
            window.removeEventListener(
                "beforeunload",
                handleBeforeUnload
            );
        };
    }, [hasChanges]);

    // ======================================================
    // Event Handlers
    // ======================================================

    const handleSave = async () => {
        if (saveLock.current) return;

        const {
            isValid,
            validationErrors
        } = validate();

        if (!isValid) {

            setErrors(validationErrors);

            notify(
                "Please complete all required fields.",
                "error"
            );

            return;
        }

        saveLock.current = true;

        const payload = {
            job_title: form.job_title,
            company: form.company,
            job_type: form.job_type,
            summary: form.summary,
            status: form.status,
            mode: form.mode,
            domain: form.domain,
            jd_url: form.jd_url,
            skill: {
                id: form.skillId,
                keySkills: form.keySkills,
                additionalSkills:
                    form.additionalSkills,
            },
            experience: {
                id: form.experienceId,
                responsibilities:
                    form.responsibilities,
            },
            education: {
                id: form.educationId,
                education:
                    form.qualifications,
            },
        };

        try {
            setSaving(true);

            const result =
                await updateJDAndGenerateQuestions(
                    {
                        id,
                        payload,
                    }
                );

            if (!result.success) {
                notify(
                    result.message ||
                    "Failed to update Job Description.",
                    "error"
                );
                return;
            }

            notify(
                result.message ||
                "Job Description updated successfully!",
                "success"
            );

            setInitialForm(form);

            if (
                result.questionResult &&
                !result.questionResult.success
            ) {
                notify(
                    result.questionResult
                        .message,
                    "warning"
                );
            }

            setModalOpen(false);

            setTimeout(() => {
                navigate(
                    "/recruiter/landing"
                );
            }, 1000);
        } catch (error) {
            console.error(error);

            notify(
                "Something went wrong while updating Job Description.",
                "error"
            );
        } finally {
            saveLock.current = false;
            setSaving(false);
        }
    };

    const handleCancel = async () => {
        if (hasChanges) {
            setCancelDialogOpen(true);
            return;
        }

        setModalOpen(false);
        navigate("/recruiter/landing");
    };

    const handleConfirmCancel = async () => {
        try {
            if (hasChanges) {
                await generateJDQuestions(id);
            }

            setCancelDialogOpen(false);
            setModalOpen(false);

            navigate("/recruiter/landing");
        } catch (error) {
            console.error(error);
            notify(
                "Something went wrong while cancelling.",
                "error"
            );
        }
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
    };

    // ======================================================
    // Loading State
    // ======================================================

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "80vh",
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }
    // ---------------- RENDER ----------------
    return (
        <Box>
            <CustomHeader title="JD Analysis" />

            {/* ===== BLOCKING MODAL ===== */}
            <Dialog
                open={modalOpen}
                TransitionComponent={SlideUp}
                keepMounted={false}
                disableEscapeKeyDown
                onClose={() => { /* intentionally blocked — use buttons only */ }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
                        m: 2,
                    },
                }}
                BackdropProps={{
                    sx: {
                        background: "rgba(15, 12, 41, 0.7)",
                        backdropFilter: "blur(6px)",
                    },
                }}
            >
                {/* ---- Top Bar ---- */}
                <Box
                    sx={{
                        px: 3, pt: 3, pb: 2.5,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        borderBottom: `1px solid ${alpha("#667eea", 0.12)}`,
                        background: `linear-gradient(135deg, ${alpha("#667eea", 0.04)} 0%, ${alpha("#764ba2", 0.04)} 100%)`,
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700}
                            sx={{ letterSpacing: "-0.02em", color: "text.primary" }}>
                            Job Description
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.3 }}>
                            <LockOutlined sx={{ fontSize: 11, color: "text.disabled" }} />
                            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.71rem" }}>
                                Please update or cancel to continue
                            </Typography>
                        </Box>
                    </Box>

                    {/* Dynamic Flow badge */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={true}
                                disabled
                                sx={{
                                    "& .MuiSwitch-track": { backgroundColor: "#667eea", opacity: "1 !important" },
                                    "& .MuiSwitch-thumb": { backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" },
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <AutoAwesome sx={{ fontSize: 14, color: "#667eea" }} />
                                <Typography variant="body2" fontWeight={600} sx={{ color: "#667eea" }}>
                                    Dynamic Flow
                                </Typography>
                                <Tooltip
                                    title={
                                        <Box sx={{ p: 0.5 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                                <WorkspacePremium sx={{ fontSize: 15, color: "#f59e0b" }} />
                                                <Typography variant="caption" fontWeight={700} sx={{ color: "#f59e0b", letterSpacing: "0.04em" }}>
                                                    PREMIUM FEATURE
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.5, display: "block" }}>
                                                Dynamic Flow is available exclusively for paid plans. Upgrade to unlock adaptive interview question generation.
                                            </Typography>
                                        </Box>
                                    }
                                    arrow
                                    placement="bottom"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: "#1e1b4b",
                                                border: "1px solid rgba(245,158,11,0.3)",
                                                borderRadius: "10px",
                                                maxWidth: 240,
                                                p: 1.25,
                                                boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                                            },
                                        },
                                        arrow: { sx: { color: "#1e1b4b" } },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 0.4,
                                            ml: 0.25, px: 0.9, py: 0.2, borderRadius: "6px",
                                            background: "linear-gradient(135deg, #f59e0b22, #f59e0b44)",
                                            border: "1px solid #f59e0b66", cursor: "pointer",
                                        }}
                                    >
                                        <WorkspacePremium sx={{ fontSize: 12, color: "#f59e0b" }} />
                                        <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#f59e0b", letterSpacing: "0.03em" }}>
                                            PRO
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                        }
                        sx={{
                            px: 2, py: 0.75, m: 0, borderRadius: "10px",
                            border: "1.5px solid", borderColor: alpha("#667eea", 0.35),
                            background: alpha("#667eea", 0.06),
                        }}
                    />
                </Box>

                {/* ---- Scrollable Form Body ---- */}
                <DialogContent
                    sx={{
                        p: 0,
                        maxHeight: "calc(100vh - 260px)",
                        overflowY: "auto",
                        scrollbarWidth: "thin",
                        scrollbarColor: `${alpha("#667eea", 0.3)} transparent`,
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: alpha("#667eea", 0.3),
                            borderRadius: 3,
                            "&:hover": { background: alpha("#667eea", 0.5) },
                        },
                    }}
                >
                    <Box sx={{ px: 3, py: 3 }}>

                        {/* ===== SECTION 1: Basic Info ===== */}
                        <SectionHeader
                            icon={<Work sx={{ fontSize: 18 }} />}
                            title="Basic Information"
                            subtitle="Core details about the job opening"
                            accent="#667eea"
                        />

                        <TwoColRow>
                            <Box>
                                <FieldLabel required>Job Title</FieldLabel>
                                <CustomInput
                                    fullWidth
                                    size="small"
                                    value={form.job_title || ""}
                                    onChange={
                                        handleChange("job_title")
                                    }
                                    onBlur={() => markTouched("job_title")}
                                    error={!!errors.job_title}
                                    helperText={errors.job_title}
                                    sx={fieldSx(!!errors.job_title)}
                                />
                            </Box>
                            <Box>
                                <FieldLabel required>Company</FieldLabel>
                                <CustomInput
                                    fullWidth size="small"
                                    placeholder="e.g. Acme Corp"
                                    value={form.company || ""}
                                    onChange={handleChange("company")}
                                    variant="outlined"
                                    error={!!errors.company}
                                    helperText={errors.company}
                                    sx={fieldSx(!!errors.company)}
                                    FormHelperTextProps={{ sx: { fontSize: "0.73rem", mt: 0.5, color: "#e53935" } }}
                                />
                            </Box>
                        </TwoColRow>

                        <Box sx={{ mt: 3 }}>
                            <TwoColRow>
                                <Box>
                                    <FieldLabel>Domain</FieldLabel>
                                    <CustomInput
                                        fullWidth
                                        size="small"
                                        value={form.domain || ""}
                                        onChange={
                                            handleChange("domain")
                                        }
                                        onBlur={() => markTouched("domain")}
                                        error={!!errors.domain}
                                        helperText={errors.domain}
                                        sx={fieldSx(!!errors.domain)}
                                    />
                                </Box>
                                <Box>
                                    <FieldLabel >Job Type</FieldLabel>
                                    <CustomInput
                                        fullWidth
                                        size="small"
                                        value={form.job_type || ""}
                                        onChange={
                                            handleChange("job_type")
                                        }
                                        onBlur={() => markTouched("job_type")}
                                        error={!!errors.job_type}
                                        helperText={errors.job_type}
                                        sx={fieldSx(!!errors.job_type)}
                                    />
                                </Box>
                            </TwoColRow>
                        </Box>

                        <SectionDivider />

                        {/* ===== SECTION 2: Skills ===== */}
                        <SectionHeader
                            icon={<Psychology sx={{ fontSize: 18 }} />}
                            title="Skills & Expertise"
                            subtitle="Define technical and functional skill expectations"
                            accent="#764ba2"
                        />

                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                                <FieldLabel required>Key Skills</FieldLabel>
                                <Chip
                                    icon={<AutoAwesome sx={{ fontSize: 12, color: "#667eea !important" }} />}
                                    label="Used for interview questions"
                                    size="small"
                                    sx={{
                                        height: 22, fontSize: "0.7rem", fontWeight: 600,
                                        background: alpha("#667eea", 0.1), color: "#667eea",
                                        border: `1px solid ${alpha("#667eea", 0.25)}`,
                                        "& .MuiChip-label": { px: 1 },
                                    }}
                                />
                            </Box>
                            <CustomInput
                                fullWidth
                                multiline
                                rows={4}
                                value={form.keySkills || ""}
                                onChange={
                                    handleChange("keySkills")
                                }
                                onBlur={() => markTouched("keySkills")}
                                error={!!errors.keySkills}
                                helperText={errors.keySkills}
                                sx={fieldSx(!!errors.keySkills)}
                            />
                            <NoteBanner
                                icon={<InfoOutlined sx={{ fontSize: 16 }} />}
                                message="Interview questions will be generated based on Key Skills only. Additional skills are used for filtering purposes and will not influence the question set."
                                accent="#667eea"
                            />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <FieldLabel>Additional Skills</FieldLabel>
                            <CustomInput
                                fullWidth multiline rows={3}
                                value={form.additionalSkills || ""}
                                onChange={handleChange("additionalSkills")}
                                placeholder="Enter additional or preferred skills (optional)"
                                variant="outlined"
                                sx={fieldSx(false)}
                            />
                        </Box>

                        <SectionDivider />

                        {/* ===== SECTION 3: Details & Requirements ===== */}
                        <SectionHeader
                            icon={<Description sx={{ fontSize: 18 }} />}
                            title="Details & Requirements"
                            subtitle="Job description, responsibilities, and qualifications"
                            accent="#06b6d4"
                        />

                        <Box>
                            <FieldLabel required>Job Description / Summary</FieldLabel>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={form.summary || ""}
                                onChange={
                                    handleChange("summary")
                                }
                                onBlur={() => markTouched("summary")}
                                error={!!errors.summary}
                                helperText={errors.summary}
                                sx={fieldSx(!!errors.summary)}
                            />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <FieldLabel required>Key Responsibilities</FieldLabel>
                            <TextField
                                fullWidth
                                multiline
                                rows={5}
                                value={form.responsibilities || ""}
                                onChange={
                                    handleChange(
                                        "responsibilities"
                                    )
                                }
                                onBlur={() =>
                                    markTouched("responsibilities")
                                }
                                error={!!errors.responsibilities}
                                helperText={errors.responsibilities}
                                sx={fieldSx(!!errors.responsibilities)}
                            />
                        </Box>

                        <Box sx={{ mt: 3, mb: 1 }}>
                            <FieldLabel required>Educational Qualifications</FieldLabel>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={form.qualifications || ""}
                                onChange={handleChange("qualifications")}
                                onBlur={() =>
                                    markTouched("qualifications")
                                }
                                error={!!errors.qualifications}
                                helperText={errors.qualifications}
                                sx={fieldSx(!!errors.qualifications)}
                            />
                        </Box>

                    </Box>
                </DialogContent>

                {/* ---- Sticky Footer Buttons ---- */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        p:1
                    }}
                >
                        <CustomButton
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            Cancel
                        </CustomButton>

                        <CustomButton
                            onClick={handleSave}
                            disabled={!canUpdate}
                        >
                            {saving
                                ? "Updating..."
                                : "Save Changes"}
                        </CustomButton>
                </Box>
            </Dialog>

            <Dialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                    },
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ mb: 1 }}
                    >
                        Discard Changes?
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        Do you really want to cancel the changes you made?
                        All unsaved modifications will be lost.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                        }}
                    >
                        <CustomButton
                            variant="outlined"
                            onClick={handleCloseCancelDialog}
                        >
                            Continue Editing
                        </CustomButton>

                        <CustomButton
                            onClick={handleConfirmCancel}
                        >
                            Discard Changes
                        </CustomButton>
                    </Box>
                </Box>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={closeSnackbar}
            />
        </Box>
    );
}
